/**
 * Created by andrey on 30.10.2020.
 */

const AdsPlugin = function (pluginName, options) {
    options = options || {};

    BasePlugin.call(this, "ads", pluginName, options);

    this.cantLoadInParallel = options.cantLoadInParallel;
    this.cantLoadAndPlayInParallel = options.cantLoadAndPlayInParallel;
    this.cantLoadAfterPlay = options.cantLoadAfterPlay;
    this.customImpressionEvent = options.customImpressionEvent;
    this.expirationPeriod = options.expirationPeriod;
    this.interstitialInterval = options.interstitialInterval;

    this.units = {};
    AdsPlugin.TYPES.forEach((name) => {
        this.units[name] = new AdUnit(this, name);
    });

    this.permissionAsked = false;
};

AdsPlugin.prototype = Object.create(BasePlugin.prototype);
AdsPlugin.prototype.constructor = AdsPlugin;

AdsPlugin.prototype.canConnect = function () {
    return this.permissionAsked;
};

AdsPlugin.prototype._listConnectTasks = function () {
    return [
        (callback) => this.loadSdkAndConnect(callback),
        (callback) => {
            connector._adsManager.loadECPMConfig();
            callback(connector.STATUS_CONNECTED);
        }
    ];
};

AdsPlugin.prototype.askPermission = function (callback) {
    if (this.permissionAsked) {
        callback();
    } else {
        this._askPermission(() => {
            this.permissionAsked = true;
            callback();
        });
    }
};

AdsPlugin.prototype._askPermission = function (callback) {
    callback();
};

AdsPlugin.prototype.onCreated = function () {
    if (!connector.config.manualPermissions) {
        connector._adsManager.askPermission();
    }
};

AdsPlugin.prototype.whichIsPlaying = function () {
    const unit = Object.values(this.units).find((unit) => unit.isRunning());
    return unit && unit.name;
};

AdsPlugin.prototype._connect = function (callback) {
    callback(connector.STATUS_CONNECTED);
};

AdsPlugin.prototype.isStickyAvailable = function () {
    return true;
};

AdsPlugin.prototype.isRewardedSupported = function () {
    return true;
};

AdsPlugin.prototype.isInterstitialSupported = function () {
    return true;
};

AdsPlugin.prototype.isStickySupported = function () {
    return false;
};

// only the platforms that sell a remove-ads product opt in
AdsPlugin.prototype.isNoAdsProductAvailable = function () {
    return false;
};

AdsPlugin.prototype.isRewardedConfigured = function () {
    return true;
};

AdsPlugin.prototype.isInterstitialConfigured = function () {
    return true;
};

AdsPlugin.prototype.isStickyConfigured = function () {
    return true;
};

AdsPlugin.prototype.isIdle = function () {
    return Object.values(this.units).every((unit) => !unit.isRunning());
};

// the only place where the two units meet: cantLoadInParallel waits for the other type's load,
// cantLoadAndPlayInParallel for this type's video
AdsPlugin.prototype.canCache = function (name) {
    if (!this.isConnected() || !connector._adsManager.isAdSupported(name)) {
        return false;
    }

    const unit = this.units[name];

    if (this.cantLoadInParallel && Object.values(this.units).some((other) => other !== unit && other.isLoading())) {
        return false;
    }

    if (this.cantLoadAndPlayInParallel && unit.isRunning()) {
        return false;
    }

    return unit.canCache();
};

// a network without a load refuses an interstitial requested sooner than its interval after the start of the last ad of any type
AdsPlugin.prototype.isThrottled = function (name) {
    if (name !== AdsPlugin.INTERSTITIAL || !this.interstitialInterval) {
        return false;
    }

    const lastStartTime = Math.max(...Object.values(this.units).map((unit) => unit.startTime || 0));
    return lastStartTime + this.interstitialInterval > Date.now();
};

AdsPlugin.prototype.cacheAd = function (name, callback) {
    this.units[name].cacheAd(callback);
};

// the load itself; MultiAdsPlugin replaces it with loading through its sub-plugins
AdsPlugin.prototype.cacheAdInner = function (name, callback) {
    this.units[name].load(callback);
};

AdsPlugin.prototype._cacheAd = function (name, callback) {
    callback(connector.CODE_SUCCEED, true);
};

AdsPlugin.prototype._abandonAd = function () {
};

AdsPlugin.prototype.isAdExpired = function () {
    return false;
};

AdsPlugin.prototype.playAd = function (name, callback) {
    this.units[name].playAd(callback);
};

AdsPlugin.prototype._playAd = function (name, ad, callback) {
    callback(connector.CODE_SUCCEED);
};

AdsPlugin.prototype.onImpressionEvent = function (name, impressionCost) {
    if (impressionCost === undefined) {
        impressionCost = this.getECPM(name) / 1000;
    }

    if (!impressionCost) {
        return;
    }

    this.bus.trigger(`${name}:impression`, impressionCost, this.pluginName);

    connector.metrics.logEvent(connector.EVENTS.ADS.TYPE_COST + name, { value: impressionCost });

    connector._predictionManager.processPrediction(connector.EVENTS.PREDICTIONS.AD_REVENUE, impressionCost);
    connector.conversions.trackAdRevenue(name, impressionCost, { source: this.pluginName });

    if (name === AdsPlugin.REWARDED) {
        connector._predictionManager.processPrediction(connector.EVENTS.PREDICTIONS.AD_REWARDED, 1);
    } else if (name === AdsPlugin.INTERSTITIAL) {
        connector._predictionManager.processPrediction(connector.EVENTS.PREDICTIONS.AD_INTERSTITIAL, 1);
    }
};

AdsPlugin.prototype.getECPM = function (name) {
    // export https://gyazo.com/57dea33693dea1f4a17a9f0d8dd27457
    // cut top counties by revenue and impressions, calculate average for default in spreadsheets after for other countries
    // rest countries average https://gyazo.com/3e3934b2261b967ddbcc549cb7a85250
    const config = window.CONNECTOR_ECPM;

    let eCPM = config[this.pluginName] && config[this.pluginName][name];

    if (!eCPM && connector.config.debugMode) {
        throw `No eCPM for ${this.pluginName} ${name}`;
    }

    if (typeof eCPM === "object") {
        eCPM = eCPM[connector._countryManager.country] || eCPM.default;
    }

    if (typeof eCPM === "string") {
        const parts = eCPM.split(" ");
        eCPM = Math.round(parseFloat(parts[0]) / connector.EXCHANGE_RATES[parts[1]] * 1000) / 1000;
    }

    return eCPM || 0.1;
};

AdsPlugin.LOADER_STATE = {
    IDLE: "idle",
    LOADING: "loading",
    READY: "ready"
};

AdsPlugin.VIDEO_STATE = {
    IDLE: "idle",
    PLAYING: "playing"
};

AdsPlugin.REWARDED = "rewarded";
AdsPlugin.INTERSTITIAL = "interstitial";
AdsPlugin.STICKY = "sticky";
AdsPlugin.TYPES = [AdsPlugin.INTERSTITIAL, AdsPlugin.REWARDED];

AdsPlugin.ERROR_LOADING_UNKNOWN = "ads_loading_unknown";
AdsPlugin.ERROR_LOADING_FAILED = "ads_loading_failed";
AdsPlugin.ERROR_LOADING_NO_ADS = "ads_loading_no_ads";
AdsPlugin.ERROR_LOADING_ADBLOCK = "ads_loading_adblock";
AdsPlugin.ERROR_LOADING_ALREADY_LOADING = "ads_loading_already_loading";
AdsPlugin.ERROR_LOADING_TIMEOUT = "ads_loading_timeout";
AdsPlugin.ERROR_LOADING_ABANDONED = "ads_loading_abandoned";

AdsPlugin.TRY_LOADING_TIMEOUT = connector.utils.parseInterval("10 seconds");
AdsPlugin.ABANDON_LOADING_TIMEOUT = connector.utils.parseInterval("90 seconds");
AdsPlugin.CHECK_CACHE_INTERVAL = connector.utils.parseInterval("30 seconds");
AdsPlugin.LOAD_AFTER_PLAY_TIME = connector.utils.parseInterval("15 seconds");
AdsPlugin.LOAD_AFTER_ERROR_TIME = connector.utils.parseInterval("2 minutes");

// back-off before the next load attempt, by the error code of the last one; anything not listed waits LOAD_AFTER_ERROR_TIME.
// transient network errors and a hung load are cheap to retry, FB throttling codes cannot succeed sooner
AdsPlugin.LOAD_ERROR_TIMEOUTS = {
    ads_loading_abandoned: connector.utils.parseInterval("30 seconds"),
    ads_loading_network_failure: connector.utils.parseInterval("15 seconds"),
    ads_loading_ads_frequent_load: connector.utils.parseInterval("3 minutes"),
    ads_loading_ads_too_many_instances: connector.utils.parseInterval("3 minutes")
};

AdsPlugin.getLoadErrorTimeout = function (code) {
    const timeout = AdsPlugin.LOAD_ERROR_TIMEOUTS[code];
    return timeout !== undefined ? timeout : AdsPlugin.LOAD_AFTER_ERROR_TIME;
};
